/**
 * notificationIcons.tsx
 *
 * Single source of truth for the notification-type → icon/colour
 * mapping. Used by both `NotificationBell` (topbar dropdown) and
 * `NotificationsPage` (full inbox at `/notifications`). Extracted
 * in v7.1 from the two near-identical copies that lived in those
 * files since v5; the v7.0 commit explicitly flagged this as a
 * follow-up.
 *
 * Adding a new mapping:
 *   1. Append a new `if (type === 'xxx')` clause below in the right
 *      ordering position (specific types BEFORE prefix matches —
 *      `kpi_blocker` is checked before `kpi_*`).
 *   2. Update the comment header above each branch with the version
 *      that introduced the type.
 *   3. The bell + inbox automatically pick up the new mapping with
 *      no further edits.
 *
 * Colour vocabulary:
 *   blue / purple / teal / orange / green / pink / yellow / indigo
 *   correspond to the 8 distinct domain families (leave / kpi / task
 *   / ticket / expense / mention / announcement / channel). Two
 *   branches use the same icon (Megaphone) in different tints to
 *   distinguish workspace-local `announcement` (yellow) from
 *   cross-workspace `platform_announcement` (orange).
 */

import React from 'react';
import {
  Bell, CalendarDays, TrendingUp, CheckSquare, LifeBuoy,
  DollarSign, AtSign, Megaphone, AlertTriangle, ShieldAlert,
  Flag, Target, Hash, ShoppingCart, UserPlus, FileSignature, Mail,
} from 'lucide-react';
import type { NotificationType } from '../../types';

export interface IconConfig {
  icon: React.ReactNode;
  bg: string;
}

/**
 * Map a notification type to its icon + colour-tint Tailwind classes.
 * Defaults to a slate `Bell` for unknown types so a backend that
 * starts emitting a new type doesn't crash the renderer — the row
 * just renders generically until the mapping is added here.
 *
 * Size defaults to 14px to match the bell + inbox row height.
 * Callers can override by passing their own icon JSX downstream;
 * this helper deliberately bakes in the size since both call sites
 * use the same.
 */
export function getNotificationIconConfig(
  type: NotificationType,
  size: number = 14,
): IconConfig {
  const sz = size;

  // Specific types BEFORE prefix matches (kpi_blocker before kpi_*).
  if (type.startsWith('leave_'))        return { icon: <CalendarDays  size={sz} />, bg: 'bg-blue-500/30 text-blue-300' };
  if (type === 'kpi_blocker')           return { icon: <Flag          size={sz} />, bg: 'bg-red-500/30 text-red-300' };
  if (type.startsWith('kpi_'))          return { icon: <TrendingUp    size={sz} />, bg: 'bg-purple-500/30 text-purple-300' };
  if (type === 'project_milestone')     return { icon: <Target        size={sz} />, bg: 'bg-emerald-500/30 text-emerald-300' };
  if (type.startsWith('task_'))         return { icon: <CheckSquare   size={sz} />, bg: 'bg-teal-500/30 text-teal-300' };
  // 2026-09-22 — a scheduled message that did not send (or may not have). Alarm-coloured
  // like `sla_breach`, and BEFORE the `mail_` prefix, or it would render as an ordinary
  // teal mail notice and read as good news.
  if (type === 'mail_send_failed')      return { icon: <AlertTriangle size={sz} />, bg: 'bg-red-500/30 text-red-300' };
  if (type.startsWith('mail_'))         return { icon: <Mail          size={sz} />, bg: 'bg-teal-500/30 text-teal-300' };
  if (type.startsWith('ticket_'))       return { icon: <LifeBuoy      size={sz} />, bg: 'bg-orange-500/30 text-orange-300' };
  // v7.29 — cron-emitted SLA breach. Distinct from `ticket_*`
  // (the recipient is the agent class, not the assignee), and
  // alarm-coloured to convey urgency. Same family as
  // `budget_threshold` below.
  if (type === 'sla_breach')            return { icon: <AlertTriangle size={sz} />, bg: 'bg-red-500/30 text-red-300' };
  if (type.startsWith('expense_'))      return { icon: <DollarSign    size={sz} />, bg: 'bg-green-500/30 text-green-300' };
  // Admin → Procurement purchase-request decisions; cart icon in a sky tint to
  // sit apart from the green expense family.
  if (type.startsWith('procurement_'))  return { icon: <ShoppingCart   size={sz} />, bg: 'bg-sky-500/30 text-sky-300' };
  if (type === 'budget_threshold')      return { icon: <AlertTriangle size={sz} />, bg: 'bg-red-500/30 text-red-300' };
  // Phase 4 — PulseBot pending user-invite request (admin-facing). Violet tint,
  // distinct from the channel indigo, to read as an account/provisioning action.
  if (type === 'user_invite_requested') return { icon: <UserPlus      size={sz} />, bg: 'bg-violet-500/30 text-violet-300' };
  if (type === 'compliance_alert')      return { icon: <ShieldAlert   size={sz} />, bg: 'bg-amber-500/30 text-amber-300' };
  // Multi-sig Stage 3 — document_signature_requested / _fully_executed /
  // _signature_declined share a pen-on-document icon in a fresh cyan tint, sat
  // apart from the green expense + sky procurement families. No existing prefix
  // starts with 'document_', so this branch is collision-free.
  if (type.startsWith('document_'))     return { icon: <FileSignature size={sz} />, bg: 'bg-cyan-500/30 text-cyan-300' };
  if (type === 'mention')               return { icon: <AtSign        size={sz} />, bg: 'bg-pink-500/30 text-pink-300' };
  if (type === 'announcement')          return { icon: <Megaphone     size={sz} />, bg: 'bg-yellow-500/30 text-yellow-300' };
  // v6.80 — cross-workspace broadcasts use the same megaphone in a
  // distinct yellow-orange tint so users can scan them apart from
  // workspace-local 'announcement' rows in the bell.
  if (type === 'platform_announcement') return { icon: <Megaphone     size={sz} />, bg: 'bg-orange-500/30 text-orange-300' };
  // v6.90-95 — channels notification quintet (member-affecting actions)
  // share the # icon in indigo. Each type has the channel name in
  // body so the user can disambiguate at a glance.
  if (type.startsWith('channel_'))      return { icon: <Hash          size={sz} />, bg: 'bg-indigo-500/30 text-indigo-300' };
  return                                       { icon: <Bell          size={sz} />, bg: 'bg-white/15 text-white/60' };
}
